import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyCessationProgramme } from './companyCessationApi';
import SuppressedPanel from './SuppressedPanel';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID
} from '../../../../component/common/designSystem';
import {
  PROGRAMME_LABELS,
  programmeColor,
  RUN_RAMP,
  TENURE_RAMP,
  INTENSITY_RAMP,
  ACTIVITY_RAMP,
  ATTEMPT_RAMP,
  RANK_RAMP
} from './cessationTheme';

/** Copy that differs between the two programmes. Everything else is shared. */
const COPY = {
  puff: {
    runTitle: 'Time Since Last Cigarette or Vape',
    slipDayLabel: 'smoke-free days',
    triggerSubtitle: 'What people say sets a craving off',
    impactTitle: 'What Went Unsmoked',
    emptyLogs: 'Nobody on Last Puff has logged anything yet.'
  },
  drink: {
    runTitle: 'Time Since Last Drink',
    slipDayLabel: 'alcohol-free days',
    triggerSubtitle: 'What people say sets a craving off',
    impactTitle: 'What Went Undrunk',
    emptyLogs: 'Nobody on Last Drink has logged anything yet.'
  }
};

/**
 * One quit programme's page — Last Puff or Last Drink.
 *
 * Both render this component, because the two features are built the same way:
 * a profile holding the baseline and a persisted run, and logs that are either
 * a daily check-in or a craving episode. Writing them twice would have meant
 * two pages that slowly stopped agreeing about what a slip is.
 *
 * The page is organised around one idea: THIS IS NOT A PASS/FAIL. Quitting is a
 * thing people do repeatedly before it sticks, so nothing here is framed as
 * success or failure. What is measured is how many people are currently clear,
 * how many days people got through, how many cravings they came out the other
 * side of, and whether they are still turning up.
 *
 * NOBODY IS NAMED. There is no drill-down from any figure on this page, and the
 * endpoint behind it has no per-person builder to drill into. A cohort under
 * the backend's minimum renders the withheld state instead of numbers.
 */
const ProgrammePage = ({
  programme,
  fetcher = null,
  populationLabel = 'users',
  department = ''
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    const load = fetcher || (() => fetchCompanyCessationProgramme(programme, department));
    load().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher, programme, department]);

  if (loader) return <Loader />;

  const copy = COPY[programme] || COPY.puff;
  const label = PROGRAMME_LABELS[programme] || 'This programme';

  if (stats?.suppressed) {
    return <SuppressedPanel minimum={stats.minimum} what={label} />;
  }
  if (!stats) return <PanelEmpty>Couldn&apos;t load {label}.</PanelEmpty>;

  const accent = programmeColor(programme);
  const trendHasData = stats.logsTrend.some((value) => value > 0);
  const cravingsHaveData = stats.weekdayCravings.some((day) => day.count > 0);

  /**
   * A `[{label, count, share}]` list drawn from the backend's own share.
   *
   * Not the design system's ProgressList, which recomputes each row's share as
   * a proportion of the rows on screen. That is wrong for every list here: a
   * top-N of triggers is a subset of all triggers, and each row's denominator
   * is the number of people, which only the backend knows.
   */
  const barList = (rows, unit, colorFor = () => accent) => (
    <ul className="space-y-4">
      {rows.map((row, index) => (
        <li key={row.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300 truncate" title={row.label}>
              {row.label}
            </span>
            <span className="text-sm font-semibold text-shoorah-btn dark:text-white shrink-0">
              {row.share}%
              <span className="ml-1.5 font-normal text-gray-400">
                {formatBigNumber(row.count)} {unit}
              </span>
            </span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-shoorah-normanGray dark:bg-shoorah-darkBgColor overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.max(row.share, row.count > 0 ? 2 : 0)}%`,
                backgroundColor: colorFor(index)
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [accent, RANK_RAMP[3]],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(stats.logsTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [accent],
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: stats.weekdayCravings.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } }
  };

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People on the Programme"
          value={formatBigNumber(stats.participants)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="A Week or More Clear"
          value={formatBigNumber(stats.slipFreeWeek)}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="A Month or More Clear"
          value={formatBigNumber(stats.slipFreeMonth)}
        />
        {/* Days people got through, not people who finished. Quitting is not a
            thing that completes on a reporting period. */}
        <StatCard
          icon={STAT_ICONS.completions}
          title="Clear Days Logged"
          value={stats.loggedDays > 0 ? `${stats.slipFreeDayRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Longest Run Reached"
          value={stats.bestRun > 0 ? `${formatBigNumber(stats.bestRun)} days` : '-'}
        />
      </div>

      {/* ---------- How the runs look ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title={copy.runTitle}
          subtitle="Where each person's current run stands"
          data={stats.runSplit}
          colors={RUN_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={6}
        />
        {/* The best run each person ever reached. It only ever moves forward,
            so a slip defers the next milestone rather than erasing what was
            already earned — the same rule the app shows the person. */}
        <MiniDonutCard
          title="Best Run So Far"
          subtitle="The longest each person has ever managed"
          data={stats.bestRunSplit}
          colors={RUN_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={6}
        />
        <MiniDonutCard
          title="How Long They've Been At It"
          subtitle="Time since they started tracking"
          data={stats.tenureSplit}
          colors={TENURE_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
      </div>

      {/* ---------- Cravings ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Craving Strength"
          subtitle="Every craving logged, on the app's 0–10 scale"
          data={stats.intensitySplit}
          colors={INTENSITY_RAMP}
          unit="logs"
          totalLabel="Cravings"
          legendLimit={5}
        />
        <Panel title="When Cravings Hit" subtitle="Craving episodes by weekday">
          {/* The most directly actionable thing on the page, and it names
              nobody: it is when to put support in front of people. */}
          {cravingsHaveData ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: 'Cravings', data: stats.weekdayCravings.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No cravings logged yet.</PanelEmpty>
          )}
        </Panel>
        <Panel title="Cravings Resisted" subtitle="Cravings logged that were not given in to">
          <div className="flex flex-col justify-center h-full py-4">
            <p className="text-4xl font-semibold text-shoorah-btn dark:text-white">
              {stats.cravings > 0 ? `${stats.cravingResistRate}%` : '-'}
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {formatBigNumber(stats.cravingsResisted)} of {formatBigNumber(stats.cravings)}{' '}
              cravings came and went without anybody {stats.slipWord}.
            </p>
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
              Only possible because a craving log records the urge and the outcome together — a slip
              count on its own would show the same programme as pure failure.
            </p>
          </div>
        </Panel>
      </div>

      {/* ---------- Staying with it ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Days Logged This Month"
          subtitle="Distinct days each person checked in"
          data={stats.activeDaySplit}
          colors={ACTIVITY_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
        <Panel title="Coming Back" subtitle="Whether people who log keep logging">
          <div className="space-y-5 py-2">
            <div>
              <p className="text-2xl font-semibold text-shoorah-btn dark:text-white">
                {stats.dailyReturnRate}%
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                of people who logged yesterday logged again today
              </p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-shoorah-btn dark:text-white">
                {stats.weeklyReturnRate}%
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                of people who logged last week logged again this week
              </p>
            </div>
          </div>
        </Panel>
        <Panel title="Signed Up, Never Started" subtitle="Onboarded but never logged anything">
          {/* A different problem from relapse, and a much more fixable one:
              these people finished a setup questionnaire and never came back. */}
          <div className="flex flex-col justify-center h-full py-4">
            <p className="text-4xl font-semibold text-shoorah-btn dark:text-white">
              {formatBigNumber(stats.neverLogged)}
            </p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              of {formatBigNumber(stats.participants)} people set the programme up and have not
              logged a single check-in or craving.
            </p>
          </div>
        </Panel>
      </div>

      {/* ---------- Trend ---------- */}
      <div className="mt-6">
        <Panel title={`${label} Over Time`} subtitle="Logs and people — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={340}
              width="100%"
              series={[
                { name: 'Logs', type: 'column', data: stats.logsTrend },
                { name: 'People', type: 'line', data: stats.peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>{copy.emptyLogs}</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Who is on it ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Previous Attempts"
          subtitle="How many times people have tried before"
          data={stats.attemptSplit}
          colors={ATTEMPT_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={5}
        />
        <Panel title={stats.durationSplit.title} subtitle="Self-reported at sign-up">
          {stats.durationSplit.rows?.length > 0 ? (
            barList(stats.durationSplit.rows, 'people', (index) => TENURE_RAMP[index])
          ) : (
            <PanelEmpty>Nobody has answered this yet.</PanelEmpty>
          )}
        </Panel>
        <Panel title="Why They Want To Stop" subtitle="From the app's own list of motivations">
          {/* Catalogue entries only. Anything typed is dropped in the pipeline
              before grouping, so nothing a person wrote can appear here. */}
          {stats.topMotivations.length > 0 ? (
            barList(stats.topMotivations, 'people', (index) => RANK_RAMP[index % RANK_RAMP.length])
          ) : (
            <PanelEmpty>
              No motivation has been chosen by enough people to report separately.
            </PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Triggers ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="What Sets Cravings Off" subtitle={copy.triggerSubtitle}>
          {/* Reported only where enough distinct people picked the same
              trigger, which is what makes it safe to show a field with no
              server-side list behind it. */}
          {stats.topTriggers.length > 0 ? (
            barList(stats.topTriggers, 'people', (index) => RANK_RAMP[index % RANK_RAMP.length])
          ) : (
            <PanelEmpty>
              No single trigger has been logged by enough people to report separately.
            </PanelEmpty>
          )}
        </Panel>
        <Panel title="Reasons for Quitting" subtitle="Chosen at sign-up">
          {stats.topReasons.length > 0 ? (
            barList(stats.topReasons, 'people', (index) => RANK_RAMP[index % RANK_RAMP.length])
          ) : (
            <PanelEmpty>No reason has been given by enough people to report separately.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Impact ---------- */}
      <div className="mt-6">
        <Panel
          title={copy.impactTitle}
          subtitle="Measured against what each person said they used before they started"
        >
          {stats.impact.totals.length > 0 || stats.impact.currencies.length > 0 ? (
            <>
              {stats.impact.currencies.length > 0 ? (
                <div className="flex flex-wrap gap-6 mb-5">
                  {/* One figure per currency, never one total. These profiles
                      each carry their own currency, and adding rupees to pounds
                      produces a number that is not money. */}
                  {stats.impact.currencies.map((row) => (
                    <div key={row.currency}>
                      <p className="text-3xl font-semibold text-shoorah-btn dark:text-white">
                        {row.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                        <span className="ml-1.5 text-base font-normal text-gray-400">
                          {row.currency}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">not spent</p>
                    </div>
                  ))}
                </div>
              ) : null}

              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Avoided</th>
                    <th className="py-2 px-4 font-medium text-center">Amount</th>
                    <th className="py-2 font-medium text-right">People counted</th>
                  </>
                }
              >
                {stats.impact.totals.map((row) => (
                  <tr key={row.key} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">{row.label}</td>
                    <td className="py-2 px-4 text-center">
                      {formatBigNumber(row.value)}
                      <span className="ml-1.5 text-gray-400">{row.unit}</span>
                    </td>
                    {/* Not everybody tracks everything — a vape-only user has
                        no cigarette baseline — so each row says how many people
                        it was actually built from. */}
                    <td className="py-2 text-right">{formatBigNumber(row.people)}</td>
                  </tr>
                ))}
              </PanelTable>
            </>
          ) : (
            <PanelEmpty>
              Nobody has given enough of a baseline at sign-up to work this out yet.
            </PanelEmpty>
          )}
        </Panel>
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          These are the same calculations the app shows each person on their own insight screen,
          summed across everyone in this view. They rest on what people said they used before they
          started, so treat them as an order of magnitude rather than an audited figure.
        </p>
      </div>

      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
        Nobody is named on this page and nothing here can be drilled into. What somebody wrote about
        their own fears at sign-up is never reported, and a craving, a slip or a run always belongs
        to the person it happened to.
      </p>
    </div>
  );
};

ProgrammePage.propTypes = {
  programme: PropTypes.oneOf(['puff', 'drink']).isRequired,
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string,
  department: PropTypes.string
};

export default ProgrammePage;
